import React, { useState, useEffect, useMemo } from "react";
import Header from "../components/Header";
import Hero from "../components/Hero";
import ServicesPreview from "../components/ServicesPreview";
import AboutSection from "../components/AboutSection";
import QualityAssurance from "../components/QualityAssurance";
import MDKSection from "../components/MDKSection";
import Certificates from "../components/Certificates";
import GoogleReviews from "../components/GoogleReviews";
import ContactSection from "../components/ContactSection";
import Footer from "../components/Footer";
import { contentAPI } from "../services/api";

// Map section_type to React component (keys must match admin section_type)
const SECTION_COMPONENTS = {
  hero: Hero,
  services: ServicesPreview,
  about: AboutSection,
  quality: QualityAssurance,
  mdk: MDKSection,
  google_reviews: GoogleReviews,
  contact: ContactSection,
  certificates: Certificates,
};

// Default order when no sections exist in DB (matches admin section order)
const DEFAULT_ORDER = [
  "hero",
  "services",
  "about",
  "quality",
  "mdk",
  "google_reviews",
  "contact",
  "certificates",
];

const HomePage = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const data = await contentAPI.getSections("home", { includeInactive: true });
        setSections(data);
      } catch (error) {
        console.error("Error fetching sections:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSections();
  }, []);

  // Build the ordered list of active sections to render
  const activeSections = useMemo(() => {
    if (sections.length === 0) {
      // No DB sections yet - use default order, all active
      return DEFAULT_ORDER;
    }

    // Build a map of section_type => section data
    const sectionMap = {};
    sections.forEach((s) => {
      sectionMap[s.section_type] = s;
    });

    // Get all section types we know about, sorted by order_index
    const allTypes = [...new Set([...sections.map((s) => s.section_type), ...DEFAULT_ORDER])];

    // Build ordered list from DB sections
    const ordered = allTypes
      .map((type) => ({
        type,
        order_index: sectionMap[type]?.order_index ?? DEFAULT_ORDER.indexOf(type),
        is_active: sectionMap[type] ? sectionMap[type].is_active !== false : true,
      }))
      .sort((a, b) => a.order_index - b.order_index)
      .filter((s) => s.is_active && SECTION_COMPONENTS[s.type])
      .map((s) => s.type);

    return ordered;
  }, [sections]);

  return (
    <div className="min-h-screen bg-[var(--vortex-bg)] w-full overflow-x-hidden">
      <Header />
      <main id="main-content" className="w-full overflow-x-hidden">
        {activeSections.map((sectionType) => {
          const Component = SECTION_COMPONENTS[sectionType];
          return Component ? <Component key={sectionType} /> : null;
        })}
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
